import { createIdentifier } from '@toeverything/infra';

import type { DocCreationLocation } from './types';

export interface LocationProvider {
  getCurrentPosition(signal: AbortSignal): Promise<DocCreationLocation>;
}

export const LocationProvider =
  createIdentifier<LocationProvider>('LocationProvider');

export class BrowserLocationProvider implements LocationProvider {
  // Browsers without the Permissions API must also avoid repeated prompts.
  private denied = false;

  async getCurrentPosition(signal: AbortSignal): Promise<DocCreationLocation> {
    if (!globalThis.navigator?.geolocation) {
      throw new Error('Geolocation is unavailable');
    }
    const permission = await navigator.permissions
      ?.query({ name: 'geolocation' })
      .catch(() => undefined);
    if (permission?.state === 'granted') this.denied = false;
    if (permission?.state === 'denied' || this.denied) {
      throw new Error('Location permission denied');
    }
    signal.throwIfAborted();
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        position => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            capturedAt: position.timestamp,
          });
        },
        error => {
          if (error.code === 1) this.denied = true;
          reject(error);
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 10_000 }
      );
    });
  }
}
